import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, apiErrorMessage } from "../../../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../ui/dialog";

/**
 * "Marchează livrate" (14.10.2026): the owner names the release that carried
 * these tickets — the tag and/or the pull request — and they move to
 * "De verificat"; who reported each one is asked by email to check it.
 */
export default function DeliverDialog({ open, labels = [], onClose, onDone }) {
  const [tag, setTag] = useState("");
  const [pr, setPr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setTag(""); setPr(""); } }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/admin/tickets/delivered", { tickets: labels, tag: tag.trim(), pr_url: pr.trim() });
      const lines = r.data.results || [];
      const done = lines.filter((l) => l.done).map((l) => l.label);
      const skipped = lines.filter((l) => !l.done);
      if (done.length) toast.success(`${done.join(", ")}: de verificat. Cei care le-au raportat primesc un email.`);
      skipped.forEach((l) => toast.error(`${l.label}: ${l.reason}`));
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Tichetele nu au putut fi marcate."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="ticket-deliver-dialog">
        <DialogHeader>
          <DialogTitle>Marchează livrate</DialogTitle>
          <DialogDescription>
            {labels.join(", ")} trec pe „De verificat”. Cei care le-au raportat primesc un email să verifice;
            fără răspuns în 7 zile, tichetul se închide singur.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="block text-xs font-semibold text-[#373A45] mb-1">Eticheta livrării</span>
            <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="2026.10.20-1"
              className="w-full px-3 py-2 text-sm bg-white border border-[#E5E7EB] rounded-lg font-mono" data-testid="ticket-deliver-tag" />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-[#373A45] mb-1">Linkul PR-ului (opțional)</span>
            <input value={pr} onChange={(e) => setPr(e.target.value)} placeholder="https://github.com/logiscool-fped/fped_ai/pull/284"
              className="w-full px-3 py-2 text-sm bg-white border border-[#E5E7EB] rounded-lg font-mono" data-testid="ticket-deliver-pr" />
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} disabled={busy}
              className="rounded-full px-4 py-2 text-sm border border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6]">Renunță</button>
            <button type="submit" disabled={busy || (!tag.trim() && !pr.trim())}
              className="rounded-full px-4 py-2 text-sm font-semibold bg-[#965EA7] text-white hover:bg-[#7A4986] disabled:opacity-60 inline-flex items-center gap-1.5"
              data-testid="ticket-deliver-submit">
              {busy && <Loader2 size={14} className="animate-spin" />} Marchează livrate
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
